import { queryClient } from "../../../queryClient";
import { MeResponse } from "../auth";

declare global {
	interface Window {
		xsrf_val: string;
	}
}

export class ErrorResponse extends Error {
	response: Response;
	data: any;

	constructor(message = "", res = new Response(), data = {}) {
		super(message);
		this.response = res;
		this.data = data;
	}
}

export const getHeaders = (options: RequestInit = {}) => {
	const csrfValue = window.xsrf_val;
	const currentUser = queryClient.getQueryData(["auth-current-user"]) as MeResponse;

	if (currentUser?.me?.authorization === null) {
		throw new Error("No authorization token found");
	}
	const authorizationToken = currentUser.me.authorization.token;

	// Fastify will error if content-type is json but there is no body (DELETE)
	const contentTypeHeader = "body" in options ? { "Content-Type": "application/json" } : {};

	return {
		"X-CSRFToken": csrfValue,
		Authorization: `Bearer ${authorizationToken}`,
		...contentTypeHeader,
		...options.headers,
	};
};

export const getUrl = (url: string) => {
	if (window.location.hostname === "localhost") {
		// if hostname is localhost, use the internal-api port
		return "http://localhost:3030" + url;
	} else {
		const split = window.location.hostname.split(".");
		const subdomain = split.slice(1).join(".");
		if (subdomain === "staging.talent.performyard.com") {
			// if it is a temp deployment point to staging
			return "https://internal-api.staging.talent.performyard.com" + url;
		} else {
			// else use the hostname prod, sandbox, staging, dev
			return "https://internal-api." + window.location.hostname + url;
		}
	}
};

// Build a custom wrapper around fetch
export const internalRequest = async <T>(url: string, options: RequestInit = {}): Promise<[T, Response]> => {
	const headers = getHeaders(options);
	const requestUrl = getUrl(url);

	const response = await fetch(requestUrl, {
		...options,
		headers: { ...headers } as Record<string, string>,
	});

	// handle 204 no content
	if (response.status === 204) {
		return [null as T, response];
	}

	const data = (await response.json()) as T;

	if (!response.ok) {
		throw new ErrorResponse(
			response.statusText ? response.statusText : "Bad fetch response",
			response,
			data as object
		);
	}

	return [data, response];
};

export const internalStreamRequest = (url: string, options: RequestInit = {}): Promise<Response> => {
	const headers = {
		...getHeaders(options),
		Accept: "application/json, text/plain, */*",
	};
	url = getUrl(url);

	return fetch(url, {
		...options,
		headers: { ...headers } as Record<string, string>,
		// Preserve abort signal if provided
		signal: options.signal,
	});
};

/**
 * Generic JSON stream processor. Reads a delimiter-separated JSON stream from a Response,
 * parses each item, and calls `onParsed` for each batch of successfully parsed items.
 *
 * Returns when the stream ends or the signal is aborted.
 *
 * @param onItem - Optional per-item transform. Return the transformed item, or `null` to skip it.
 * @param onParsed - Called with each batch of parsed (and transformed) items.
 */
export const processJsonStream = async <T>({
	response,
	delimiter,
	signal,
	onItem,
	onParsed,
}: {
	response: Response;
	delimiter: string;
	signal: AbortSignal;
	onItem?: (parsed: any) => T | null;
	onParsed: (items: T[]) => void;
}) => {
	const reader = response.body?.getReader();
	if (!reader) {
		throw new Error("Stream response has no reader");
	}

	const decoder = new TextDecoder("utf-8");
	let hasMore = true;
	let incomplete = "";

	while (hasMore && !signal.aborted) {
		const { done, value } = await reader.read();

		let chunk = decoder.decode(value, { stream: true });

		if (incomplete !== "") {
			chunk = incomplete + chunk;
			incomplete = "";
		}

		const data = chunk.split(delimiter);
		const parsedData: T[] = [];

		for (const item of data) {
			if (item === "") continue;
			try {
				const parsed = JSON.parse(item);
				if (onItem) {
					const transformed = onItem(parsed);
					if (transformed !== null) parsedData.push(transformed);
				} else {
					parsedData.push(parsed as T);
				}
			} catch {
				incomplete += item;
			}
		}

		hasMore = !done;

		if (!signal.aborted && parsedData.length > 0) {
			onParsed(parsedData);
		}
	}
};

export default internalRequest;
